import React, { useState, useEffect, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { CircularProgress } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'

import { objLength } from '../../../utils/customMethods'
import { setMessages as setStoreMessages } from '../../../redux/historyConversation/actions'
import { setAllConversations } from '../../../redux/historyConversation/actions'
import messagesApi from '../../../services/messages'
import BalloonSystem from '../../atoms/balloons/balloonSystem'
import BalloonConsumer from '../../atoms/balloons/balloonConsumer'
import BalloonTime from '../../atoms/balloons/balloonTime'
import BalloonAgent from '../../atoms/balloons/balloonAgent'
import Button from '../../atoms/inputs/button'

import useStyles from './historyConversationStyle'

const HistoryConversation = (props) => {
  const defaultStatus = {
    loading: false,
    success: false,
    error: false,
    message: '',
  }

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()
  const history = useSelector((state) => state.history)
  const [status, setStatus] = useState({
    get_conversations: defaultStatus,
    get_messages: defaultStatus,
  })
  const [messages, setMessages] = useState(null)
  const [chatId, setChatId] = useState(null)
  const qtdDefault = 15

  const updateStatus = (action, data) => {
    if (action && data)
      setStatus((e) => ({ ...e, [action]: { ...defaultStatus, ...data } }))
  }

  const getChatHistory = async () => {
    if (!status.get_conversations.loading) {
      updateStatus('get_conversations', { loading: true })
      try {
        const response = await messagesApi.get(
          `/tech4h-messages/consumer/${props?.consumerId}/conversations/${props?.channel}`
        )

        if (response.status === 200) {
          let message = response?.data?.message
          let chats = response?.data?.data?.reduce((chats, chat) => {
            return { ...chats, [chat.conversationId]: chat }
          }, {})
          updateStatus('get_conversations', { success: true, message: message })
          dispatch(setAllConversations(props?.consumerId, chats))
        } else {
          throw response.data
        }
      } catch (error) {
        let message =
          error?.response?.status === 500
            ? 'Unavailable service'
            : error?.response?.data?.message
        updateStatus('get_conversations', { error: true, message: message })
      }
    }
  }

  const getChatMessages = async (conversationId, page) => {
    updateStatus('get_messages', { loading: true })
    try {
      const response = await messagesApi.get(
        `/tech4h-messages/message` +
          `?${
            (conversationId ? `conversationId=${conversationId}` : '') +
            (page ? `&page=${page}` : '') +
            `&qtd=${qtdDefault}`
          }`
      )

      if (response.status === 200) {
        const msgs = {
          consumerId: props?.consumerId,
          chatId: conversationId,
          data: response?.data?.data?.messages,
        }
        const message = response?.data?.message

        dispatch(setStoreMessages(msgs))
        updateStatus('get_messages', { success: true, message: message })
      } else {
        throw response.data
      }
    } catch (error) {
      let message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : error?.response?.data?.message
      updateStatus('get_messages', { error: true, message: message })
    }
  }

  const firstLoad = useCallback(() => {
    return (
      !messages &&
      (status.get_conversations.loading || status.get_messages.loading) && (
        <div className={classes.loadingSection}>
          <CircularProgress size={24} />
        </div>
      )
    )
  }, [status, messages])

  // TODO: ask to backend add a count in api payload, and add this method in dedicated file (messages)
  const isLastMsg = useCallback(() => {
    const totalMessages = messages?.length
    if (!messages) {
      return false
    } else if (
      (totalMessages > 0 && messages[totalMessages - 1]?.type === 'eof') ||
      totalMessages % qtdDefault !== 0
    ) {
      return true
    }
    return false
  }, [messages])

  const renderLoadMore = useCallback(() => {
    if (messages && !isLastMsg())
      return (
        <Button
          size="large"
          variant="contained"
          color="primary"
          fullWidth
          disabled={status.get_messages.loading}
          onClick={() => {
            const totalMessages = messages.length
            const page = totalMessages / qtdDefault + 1
            getChatMessages(chatId, page, true)
          }}
        >
          {status.get_messages.loading ? (
            <CircularProgress className={classes.spinning} size={24} />
          ) : (
            t('buttons.loadMessages')
          )}
        </Button>
      )
  }, [messages, chatId, status, isLastMsg, getChatMessages])

  const renderBallonBySenderType = (el, userList) => {
    if (el.by === 'system' || el.by === 'bot') {
      return (
        <BalloonSystem
          data={el}
          showBookmark={false}
          resize={true}
          anchor={props.anchor}
        />
      )
    }
    if (el.by === 'consumer') {
      const userConsumer =
        history?.conversations?.[props?.consumerId]?.[chatId]?.consumer
      return (
        <BalloonConsumer
          data={el}
          showBookmark={false}
          resize={true}
          anchor={props.anchor}
          consumer={userConsumer}
        />
      )
    }
    if (el.by === 'agent' || el.by === 'manager') {
      return (
        <BalloonAgent
          data={el}
          showBookmark={false}
          resize={true}
          anchor={props.anchor}
          userList={userList}
        />
      )
    }
  }

  const renderMessages = useCallback(() => {
    const usersHistory =
      history?.conversations?.[props?.consumerId]?.[chatId]?.users
    if (messages?.length > 0) {
      let dividerText = null
      return messages?.map((el, key) => {
        if (
          !dividerText ||
          dividerText !== moment(el.sentTime).format('DD/MM')
        ) {
          dividerText = moment(el.sentTime).format('DD/MM')
          return (
            <div key={key}>
              <BalloonTime time={dividerText} />
              {renderBallonBySenderType(el, usersHistory)}
            </div>
          )
        } else {
          return (
            <div key={key}>{renderBallonBySenderType(el, usersHistory)}</div>
          )
        }
      })
    }
  }, [props?.consumerId, messages, chatId])

  useEffect(() => {
    if (props?.chatId) {
      setChatId(props.chatId)

      if (!objLength(history?.conversations?.[props?.consumerId]) > 0) {
        getChatHistory()
      }

      if (history.messages) {
        if (!history.messages[props.chatId]) {
          getChatMessages(props?.chatId, 1)
        }
        setMessages(history.messages[props.chatId])
      }
    } else {
      setMessages(null)
    }
  }, [props?.chatId, props?.consumerId, history.conversation, history.messages])

  return (
    <div className={classes.content}>
      {firstLoad()}
      {renderLoadMore()}
      {renderMessages()}
    </div>
  )
}

export default memo(HistoryConversation)
